import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Droplets, Layers, ShieldCheck, Star, Phone, Waves, MessageSquare } from "lucide-react";
import { Button, ButtonLink } from "@/components/site/Button";
import { PageHero } from "@/components/site/PageHero";
import { LandingVideo } from "@/components/landing/LandingVideo";
import { LandingLeadForm } from "@/components/landing/LandingLeadForm";
import {
  LandingHeader,
  LandingFooter,
  LandingStickyBar,
} from "@/components/landing/LandingChrome";
import {
  captureAttribution,
  trackPhoneClick,
  type LandingAttribution,
} from "@/lib/landing-tracking";

const PHONE_LABEL = "(480) 941-5373";
const TEL_HREF = "tel:+14809415373";
const CANONICAL = "https://www.durafoaminc.com/monsoon-roof-inspection";

export const Route = createFileRoute("/monsoon-roof-inspection")({
  head: () => ({
    meta: [
      { title: "Free Monsoon Roof Inspection in Phoenix | Durafoam Roofing" },
      {
        name: "description",
        content:
          "Get a free, no-obligation monsoon roof inspection from Durafoam Roofing. We check drains, flashing, penetrations and ponding before the next Arizona storm.",
      },
      // Ad landing page: keep it out of search so it never competes with
      // /free-roof-inspection-phoenix, but let link equity flow.
      { name: "robots", content: "noindex, follow" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Free Monsoon Roof Inspection in Phoenix" },
      {
        property: "og:description",
        content:
          "Durafoam Roofing will inspect your roof at no cost and show you exactly what we find before the next monsoon.",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: MonsoonLandingPage,
});

const BENEFITS = [
  {
    icon: Droplets,
    title: "Clogged Drains & Scuppers",
    body: "We check drainage paths for debris and conditions that can cause water to back up on the roof.",
  },
  {
    icon: Layers,
    title: "Cracks, Flashing & Penetrations",
    body: "We inspect common leak points around walls, skylights, vents, seams and roof penetrations.",
  },
  {
    icon: Waves,
    title: "Ponding & Roof Condition",
    body: "We look for standing-water issues, damaged coatings, exposed foam, deterioration and other warning signs.",
  },
];

const TRUST = [
  "Family owned since 1989",
  "ROC licensed (ROC223900 L-42)",
  "4.8 stars across 84 Google reviews",
  "No-obligation inspection",
  "A real on-roof inspection — not a phone estimate",
];

function MonsoonLandingPage() {
  const [attribution, setAttribution] = useState<LandingAttribution>(() => ({
    landing_page: "/monsoon-roof-inspection",
    referrer: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    fbclid: "",
    fbp: "",
    fbc: "",
  }));
  const [debug, setDebug] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    const attr = captureAttribution();
    setAttribution(attr);
    // Troubleshooting only: requires both flags, so a stray shared link or a
    // crawler can't surface the tracking panel for normal visitors.
    const params = new URLSearchParams(window.location.search);
    const isDebug = params.get("debug") === "1" && params.get("k") === "durafoam";
    if (isDebug) {
      setDebug({
        pageViewFired: typeof window.fbq === "function",
        attribution: attr,
        leadStored: false,
        pixelLeadFired: false,
      });
    }
  }, []);

  const onPhoneClick = useCallback(
    (kind: "call" | "text") => {
      trackPhoneClick(kind, attribution);
      setDebug((d) => (d ? { ...d, phoneClickFired: kind } : d));
    },
    [attribution],
  );

  const pushDebug = useCallback((patch: Record<string, unknown>) => {
    setDebug((d) => (d ? { ...d, ...patch } : d));
  }, []);

  return (
    <div className="pb-20 md:pb-0">
      <LandingHeader onPhoneClick={onPhoneClick} />

      {/* HERO */}
      <PageHero
        compact
        eyebrow="Free Monsoon Roof Inspection"
        h1="Is Your Roof Ready for the Next Arizona Monsoon?"
        subhead={
          <>
            Small roof problems can become major leaks when the next storm hits. We&apos;ll inspect
            your roof at no cost and show you exactly what we find.
          </>
        }
        actions={
          <>
            <ButtonLink href="#book" size="lg" className="text-lg">
              Get My Free Roof Inspection
            </ButtonLink>
            <ButtonLink
              href={TEL_HREF}
              variant="ghost-light"
              size="lg"
              onClick={() => onPhoneClick("call")}
            >
              <Phone className="h-5 w-5" aria-hidden="true" /> Call {PHONE_LABEL}
            </ButtonLink>
            <ButtonLink
              href="sms:+14809415373"
              variant="ghost-light"
              size="lg"
              className="md:hidden"
              onClick={() => onPhoneClick("text")}
            >
              <MessageSquare className="h-5 w-5" aria-hidden="true" /> Click to Text Now
            </ButtonLink>
          </>
        }
      >
        <p className="mt-4 text-sm text-white/75">
          <span aria-hidden="true">★★★★★</span> Google Rated &bull; Family Owned Since 1989 &bull;
          ROC Licensed &bull; No Obligation
        </p>
      </PageHero>


      {/* VIDEO */}
      <section className="bg-white py-10 sm:py-14">
        <div className="mx-auto grid max-w-5xl items-center gap-8 px-4 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              Sometimes a major roof problem starts this simply.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-primary/80">
              This entire pond of standing water was caused by a clogged roof drain. During an
              Arizona monsoon, leaves and debris can block drains and scuppers and allow water to
              back up around skylights, vents and other roof penetrations. A simple inspection
              before the storm can catch problems like this before they become expensive leaks.
            </p>
            <ButtonLink href="#book" size="lg" className="mt-6 hidden md:inline-flex">
              Schedule My Free Inspection
            </ButtonLink>
          </div>
          <LandingVideo />
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-[color:var(--surface,#F6F7FB)] py-10 sm:py-14">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-center text-2xl font-bold text-primary sm:text-3xl">
            What We Look For Before the Next Storm
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-primary/10 bg-white p-5">
                <Icon className="h-7 w-7 text-primary" aria-hidden="true" />
                <h3 className="mt-3 text-lg font-bold text-primary">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-primary/75">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-white py-10 sm:py-12">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-center text-xl font-bold text-primary sm:text-2xl">
            What Valley Homeowners Say About Durafoam
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-primary/10 bg-[color:var(--surface,#F6F7FB)] p-5">
              <div className="flex items-center gap-1 text-[#F47920]">
                <span aria-hidden="true">★★★★★</span>
                <span className="ml-1 text-xs font-semibold text-primary/70">Google Review</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-primary/90">
                &ldquo;Durafoam came out, found the source of our leak and explained exactly what
                needed to be done. Professional, honest and easy to work with.&rdquo;
              </p>
            </div>
            <div className="rounded-2xl border border-primary/10 bg-[color:var(--surface,#F6F7FB)] p-5">
              <div className="flex items-center gap-1 text-[#F47920]">
                <span aria-hidden="true">★★★★★</span>
                <span className="ml-1 text-xs font-semibold text-primary/70">Google Review</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-primary/90">
                &ldquo;They inspected our roof and didn&rsquo;t try to sell us something we didn&rsquo;t
                need. Highly recommend Durafoam.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="book" className="scroll-mt-6 bg-white py-10 sm:py-14">
        <div className="mx-auto max-w-2xl px-4">
          <LandingLeadForm attribution={attribution} onDebug={pushDebug} />
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-primary/[0.04] py-8">
        <div className="mx-auto max-w-4xl px-4">
          <ul className="grid gap-3 sm:grid-cols-2">
            {TRUST.map((t) => (
              <li key={t} className="flex items-start gap-2 text-primary">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <span className="text-sm font-medium leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 flex items-center gap-2 text-sm text-primary/75">
            <Star className="h-4 w-4 fill-current text-primary" aria-hidden="true" />
            4.8-star average from 84 Google reviews
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-12 text-white">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Don&apos;t Wait Until the Water Is Coming Through the Ceiling.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">
            A quick roof inspection now could catch a simple problem before the next monsoon turns
            it into a major leak.
          </p>
          <ButtonLink href="#book" size="lg" className="mt-6 text-lg">
            Schedule My Free Inspection
          </ButtonLink>
          <p className="mt-5 text-white/85">
            Call or Text{" "}
            <a href={TEL_HREF} onClick={() => onPhoneClick("call")} className="font-bold underline">
              {PHONE_LABEL}
            </a>
          </p>
        </div>
      </section>

      <LandingFooter onPhoneClick={onPhoneClick} />
      <LandingStickyBar onPhoneClick={onPhoneClick} />

      {debug ? (
        <div className="fixed left-2 top-2 z-[60] max-w-[320px] overflow-auto rounded-lg bg-black/85 p-3 text-[11px] leading-snug text-green-300">
          <div className="mb-1 flex items-center justify-between gap-2">
            <strong className="text-white">Tracking debug</strong>
            <Button
              type="button"
              variant="ghost-light"
              className="min-h-0 px-2 py-0.5 text-[11px]"
              onClick={() => setDebug(null)}
            >
              close
            </Button>
          </div>
          <pre className="whitespace-pre-wrap break-all">{JSON.stringify(debug, null, 1)}</pre>
        </div>
      ) : null}
    </div>
  );
}
